import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../context/AppContext";
import { useCopy } from "../../context/CopyContext";
import Header from "../Header";
import Heading from "../Heading";
import Button from "../Button";
import PoweredBy from "../PoweredBy";
import ItemsModalView from "./ItemsModalView";

const RateReturnsView: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useAppContext();
  const { getCopy } = useCopy();
  const [showItemsModal, setShowItemsModal] = React.useState(false);

  const handleBack = () => {
    navigate("/summary");
  };

  const handleNoThanks = () => {
    console.log("User declined rating, proceeding to confirmation");
    navigate("/confirmation");
  };

  const handleGetStarted = () => {
    console.log("User wants to rate returns, proceeding to product rating screen");
    navigate("/product-rating");
  };

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  return (
    <div
      className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden"
      data-node-id="1:104"
    >
      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col gap-10">
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={state.itemsCount > 0}
              itemsNumber={state.itemsCount.toString()}
              onItemsClick={handleItemsClick}
            />
          </div>
        </div>

        {/* Main Content Area - with 3-row grid */}
        <motion.div 
          className="flex-1 grid grid-rows-3 gap-0 mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Row 1 - Empty space */}
          <div className="flex items-center justify-center">
            {/* Empty space */}
          </div>

          {/* Row 2 - Main heading (vertically centered) */}
          <div className="flex items-center justify-center">
            <div className="text-center w-full max-w-[920px]">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ 
                  duration: 0.6, 
                  ease: [0.25, 0.1, 0.25, 1]
                }}
              >
                <Heading 
                  title={getCopy('rateReturns.heading')}
                  subtitle={getCopy('rateReturns.subheading')}
                />
              </motion.div>
            </div>
          </div>

          {/* Row 3 - Empty space for layout balance */}
          <div className="flex flex-col gap-8 justify-end">
            {/* Empty space - maintaining the 3-row grid structure */}
          </div>
        </motion.div>

        {/* Bottom Section - Buttons and footer */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Buttons */}
          <div className="flex flex-row gap-10 w-full">
            <Button variant="secondary" onClick={handleNoThanks}>
              {getCopy('rateReturns.buttons.secondary')}
            </Button>
            <Button variant="primary" onClick={handleGetStarted}>
              {getCopy('rateReturns.buttons.primary')}
            </Button>
          </div>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>

      {/* Items Modal */}
      <ItemsModalView
        isOpen={showItemsModal}
        onClose={handleCloseItemsModal}
      />
    </div>
  );
};

export default RateReturnsView;
